<template>
  <div class="fill-height">
    <v-container fluid class="container-main">
      <v-row v-if="loading" justify="center" class="pt-8">
        <v-col cols="12" sm="10" md="8" lg="6">
          <v-card flat class="text-center pa-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            ></v-progress-circular>
            <p class="text-body-1 mt-4 text--secondary">Loading your profile...</p>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-else-if="error" justify="center" class="pt-8">
        <v-col cols="12" sm="10" md="8" lg="6">
          <v-card flat class="pa-6">
            <h2>Unable to load profile</h2>
            <p class="text-body-1 text--secondary mt-2">{{ errorMessage }}</p>
            <v-btn @click="loadProfile()" outlined class="mt-4">Try Again</v-btn>
          </v-card>
        </v-col>
      </v-row>

      <template v-else>
        <v-row>
          <v-col cols="12" md="4">
            <v-card class="pa-4 mb-4">
              <div class="d-flex align-center mb-3">
                <v-avatar color="primary" size="56" class="mr-3">
                  <span class="white--text text-h5">{{ initials }}</span>
                </v-avatar>
                <div>
                  <h2 class="text-h6">{{ profile.displayName || 'Your Profile' }}</h2>
                  <p class="text-body-2 text--secondary mb-0">{{ profile.location }}</p>
                </div>
              </div>
              <v-divider class="my-3"></v-divider>
              <div v-if="profile.email" class="d-flex align-center mb-2">
                <v-icon small class="mr-2">mdi-email</v-icon>
                <span class="text-body-2">{{ profile.email }}</span>
              </div>
              <div v-if="profile.phone" class="d-flex align-center mb-2">
                <v-icon small class="mr-2">mdi-phone</v-icon>
                <span class="text-body-2">{{ profile.phone }}</span>
              </div>
              <div v-if="profile.linkedIn" class="d-flex align-center">
                <v-icon small class="mr-2">mdi-linkedin</v-icon>
                <span class="text-body-2">{{ profile.linkedIn }}</span>
              </div>
            </v-card>

            <v-card class="pa-4 mb-4">
              <h3 class="text-subtitle-1 mb-3">Profile Completeness</h3>
              <v-progress-linear
                :value="completeness.percentage"
                color="primary"
                height="12"
                rounded
              ></v-progress-linear>
              <p class="text-body-2 text--secondary mt-2 mb-0">
                {{ completeness.percentage }}% complete
              </p>
              <v-list dense class="mt-2">
                <v-list-item
                  v-for="item in completeness.items"
                  :key="item.item"
                  class="px-0"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon
                      small
                      :color="item.complete ? 'success' : 'grey'"
                    >
                      {{ item.complete ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">
                      {{ item.item }}
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action class="text-caption text--secondary">
                    {{ item.points }} pts
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>

            <v-card class="pa-4">
              <h3 class="text-subtitle-1 mb-3">Quick Stats</h3>
              <div class="d-flex flex-wrap">
                <div class="stat-item mr-4 mb-2">
                  <div class="text-h5 primary--text">{{ counts.skills }}</div>
                  <div class="text-caption text--secondary">Skills</div>
                </div>
                <div class="stat-item mr-4 mb-2">
                  <div class="text-h5 primary--text">{{ counts.workExperience }}</div>
                  <div class="text-caption text--secondary">Jobs</div>
                </div>
                <div class="stat-item mr-4 mb-2">
                  <div class="text-h5 primary--text">{{ counts.achievements }}</div>
                  <div class="text-caption text--secondary">Achievements</div>
                </div>
                <div class="stat-item mb-2">
                  <div class="text-h5 primary--text">{{ counts.education }}</div>
                  <div class="text-caption text--secondary">Education</div>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="8">
            <v-card class="pa-4 mb-4" v-if="skills.allCount > 0">
              <h3 class="text-subtitle-1 mb-3">
                <v-icon small class="mr-1">mdi-star</v-icon>
                Skills
              </h3>
              <div v-if="skills.hard && skills.hard.length > 0" class="mb-3">
                <div class="text-caption text--secondary mb-2">Hard Skills</div>
                <v-chip
                  v-for="skill in skills.hard"
                  :key="skill.id"
                  small
                  outlined
                  class="mr-1 mb-1"
                >
                  {{ skill.name }}
                </v-chip>
              </div>
              <div v-if="skills.soft && skills.soft.length > 0" class="mb-3">
                <div class="text-caption text--secondary mb-2">Soft Skills</div>
                <v-chip
                  v-for="skill in skills.soft"
                  :key="skill.id"
                  small
                  outlined
                  color="secondary"
                  class="mr-1 mb-1"
                >
                  {{ skill.name }}
                </v-chip>
              </div>
              <div v-if="skills.technical && skills.technical.length > 0">
                <div class="text-caption text--secondary mb-2">Technical Skills</div>
                <v-chip
                  v-for="skill in skills.technical"
                  :key="skill.id"
                  small
                  outlined
                  color="info"
                  class="mr-1 mb-1"
                >
                  {{ skill.name }}
                </v-chip>
              </div>
            </v-card>

            <v-card class="pa-4 mb-4" v-if="workExperience.length > 0">
              <h3 class="text-subtitle-1 mb-3">
                <v-icon small class="mr-1">mdi-briefcase</v-icon>
                Work Experience
              </h3>
              <v-timeline dense align-top>
                <v-timeline-item
                  v-for="job in workExperience"
                  :key="job.id"
                  small
                  color="primary"
                >
                  <div>
                    <div class="font-weight-bold">{{ job.title || job.role }}</div>
                    <div class="text-body-2">{{ job.company }}</div>
                    <div class="text-caption text--secondary">
                      {{ formatDateRange(job.startDate, job.endDate, job.isCurrent) }}
                      <span v-if="job.location"> &bull; {{ job.location }}</span>
                    </div>
                    <div v-if="job.achievements && job.achievements.length > 0" class="mt-2">
                      <div class="text-caption font-weight-medium mb-1">Key Achievements:</div>
                      <ul class="text-body-2 pl-4 mb-0">
                        <li
                          v-for="achievement in job.achievements.slice(0, 3)"
                          :key="achievement.id"
                          class="mb-1"
                        >
                          {{ achievement.summary }}
                        </li>
                      </ul>
                      <v-btn
                        v-if="job.achievements.length > 3"
                        x-small
                        text
                        color="primary"
                        class="mt-1"
                      >
                        +{{ job.achievements.length - 3 }} more
                      </v-btn>
                    </div>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-card>

            <v-card class="pa-4 mb-4" v-if="education.length > 0">
              <h3 class="text-subtitle-1 mb-3">
                <v-icon small class="mr-1">mdi-school</v-icon>
                Education
              </h3>
              <div
                v-for="edu in education"
                :key="edu.id"
                class="mb-3 pb-3"
                :class="{ 'border-bottom': education.indexOf(edu) < education.length - 1 }"
              >
                <div class="font-weight-bold">{{ edu.degree }} {{ edu.field ? `in ${edu.field}` : '' }}</div>
                <div class="text-body-2">{{ edu.institution }}</div>
                <div class="text-caption text--secondary">
                  {{ formatDate(edu.graduationDate) }}
                  <span v-if="edu.gpa"> &bull; GPA: {{ edu.gpa }}{{ edu.gpaScale ? `/${edu.gpaScale}` : '' }}</span>
                </div>
                <div v-if="edu.honors && edu.honors.length > 0" class="mt-1">
                  <v-chip
                    v-for="honor in edu.honors"
                    :key="honor"
                    x-small
                    color="warning"
                    class="mr-1"
                  >
                    {{ honor }}
                  </v-chip>
                </div>
              </div>
            </v-card>

            <v-row>
              <v-col cols="12" md="6" v-if="certifications.length > 0 || credentials.length > 0">
                <v-card class="pa-4 mb-4">
                  <h3 class="text-subtitle-1 mb-3">
                    <v-icon small class="mr-1">mdi-certificate</v-icon>
                    Certifications &amp; Credentials
                  </h3>
                  <div v-if="credentials.length > 0" class="mb-2">
                    <v-chip
                      v-for="(cred, idx) in credentials"
                      :key="'cred-' + idx"
                      small
                      :color="getCredentialStatusColor(cred.status)"
                      :outlined="cred.status === 'expired'"
                      class="mr-1 mb-1"
                    >
                      {{ cred.name }}
                      <span v-if="cred.jurisdiction" class="ml-1 text-caption">({{ cred.jurisdiction }})</span>
                      <v-icon v-if="cred.status === 'active'" x-small class="ml-1">mdi-check-circle</v-icon>
                      <v-icon v-else-if="cred.status === 'expired'" x-small class="ml-1">mdi-alert-circle</v-icon>
                    </v-chip>
                  </div>
                  <div v-if="certifications.length > 0 && credentials.length > 0" class="text-caption text--secondary mb-2">Legacy Entries</div>
                  <v-chip
                    v-for="cert in certifications"
                    :key="cert.id"
                    small
                    class="mr-1 mb-1"
                  >
                    {{ cert.name }}
                  </v-chip>
                </v-card>
              </v-col>
              <v-col cols="12" md="6" v-if="languages.length > 0">
                <v-card class="pa-4 mb-4">
                  <h3 class="text-subtitle-1 mb-3">
                    <v-icon small class="mr-1">mdi-translate</v-icon>
                    Languages
                  </h3>
                  <div
                    v-for="lang in languages"
                    :key="lang.id"
                    class="d-flex justify-space-between text-body-2 mb-1"
                  >
                    <span>{{ lang.name }}</span>
                    <span class="text--secondary">{{ lang.proficiency }}</span>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <v-card class="pa-4" v-if="smallWins.length > 0">
              <h3 class="text-subtitle-1 mb-3">
                <v-icon small class="mr-1">mdi-trophy</v-icon>
                Recent Wins
              </h3>
              <v-list dense>
                <v-list-item
                  v-for="win in smallWins.slice(0, 5)"
                  :key="win.id"
                  class="px-0"
                >
                  <v-list-item-icon class="mr-2">
                    <v-chip x-small :color="getWinTypeColor(win.parsedType)">
                      {{ win.parsedType }}
                    </v-chip>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">
                      {{ win.rawInput }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ formatDate(win.createdAt) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-btn
                v-if="smallWins.length > 5"
                small
                text
                color="primary"
                @click="nav('/SmallWinsProjects')"
              >
                View all {{ smallWins.length }} wins
              </v-btn>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12" class="d-flex justify-center">
            <v-btn outlined class="mr-2" @click="nav('/Information')">
              <v-icon small class="mr-1">mdi-pencil</v-icon>
              Edit Profile
            </v-btn>
            <v-btn color="primary" @click="nav('/SmallWinsCapture')">
              <v-icon small class="mr-1">mdi-plus</v-icon>
              Add Achievement
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </div>
</template>

<script>
import { GetProfileContext } from "@/api/ProfileContextApi";
import { mapFields } from "vuex-map-fields";

export default {
  name: "ProfileShowcase",
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: "",
      profile: {},
      skills: { hard: [], soft: [], technical: [], allCount: 0 },
      workExperience: [],
      education: [],
      certifications: [],
      credentials: [],
      languages: [],
      smallWins: [],
      completeness: { score: 0, maxScore: 100, percentage: 0, items: [] },
      counts: {
        skills: 0,
        workExperience: 0,
        education: 0,
        certifications: 0,
        languages: 0,
        smallWins: 0,
        achievements: 0
      }
    };
  },
  computed: {
    ...mapFields(["resumeId"]),
    initials() {
      if (!this.profile.displayName) return "?";
      const parts = this.profile.displayName.split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return this.profile.displayName.substring(0, 2).toUpperCase();
    }
  },
  async mounted() {
    await this.loadProfile();
  },
  methods: {
    async loadProfile() {
      this.loading = true;
      this.error = false;
      
      try {
        const result = await GetProfileContext(this.resumeId);
        
        if (!result.success) {
          this.error = true;
          this.errorMessage = result.msg || "Failed to load profile";
          return;
        }
        
        const data = result.data;
        this.profile = {
          displayName: data.displayName,
          email: data.email,
          phone: data.phone,
          location: data.location,
          linkedIn: data.linkedIn
        };
        this.skills = data.skills || { hard: [], soft: [], technical: [], allCount: 0 };
        this.workExperience = data.workExperience || [];
        this.education = data.education || [];
        this.certifications = data.certifications || [];
        this.credentials = data.credentials || [];
        this.languages = data.languages || [];
        this.smallWins = data.smallWins || [];
        this.completeness = data.completeness || { score: 0, maxScore: 100, percentage: 0, items: [] };
        this.counts = data.counts || {};
      } catch (e) {
        this.error = true;
        this.errorMessage = "An unexpected error occurred";
        console.error("ProfileShowcase load error:", e);
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short" });
    },
    formatDateRange(start, end, isCurrent) {
      const startStr = this.formatDate(start);
      if (isCurrent) return `${startStr} - Present`;
      const endStr = this.formatDate(end);
      return `${startStr} - ${endStr}`;
    },
    getCredentialStatusColor(status) {
      const colors = {
        active: "success",
        expiring: "warning",
        expired: "error"
      };
      return colors[(status || "").toLowerCase()] || "grey";
    },
    getWinTypeColor(type) {
      const colors = {
        achievement: "success",
        skill: "info",
        note: "grey",
        project: "warning"
      };
      return colors[type] || "grey";
    },
    nav(route) {
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e5e7eb;
}
.stat-item {
  text-align: center;
  min-width: 60px;
}
</style>
